/**
 * Workspace Assistant Context Provider
 * 
 * Provides context awareness for the Workspace AI Assistant.
 * Tracks user's current tab, selected conversation, and UI state
 * to enable intelligent, contextual assistance.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

// Generate a unique session ID
const generateSessionId = () => {
  return `workspace-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Default settings
const DEFAULT_SETTINGS = {
  proactiveSuggestions: true,
  contextualHints: true,
  autoScroll: true,
  suggestionFrequency: 'sometimes'
};

// ============================================
// CONTEXT DEFINITION
// ============================================

const WorkspaceAssistantContext = createContext(null);

// ============================================
// CONTEXT PROVIDER
// ============================================

export const WorkspaceAssistantProvider = ({ children }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  
  // Panel visibility state - now embedded in right panel, so always visible based on tab
  const [isAssistantActive, setIsAssistantActive] = useState(true);
  
  // Session ID for conversation persistence
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  
  // Current workspace context - what the user is doing
  const [workspaceContext, setWorkspaceContext] = useState({
    activeTab: null,               // e.g., 'caseDashboard', 'conversation', 'applicationReview', 'history'
    selectedUser: null,            // Currently selected user/conversation
    threadId: null,                // Current conversation thread ID
    rightPanelMode: null,          // What's showing in the right panel
    uiState: {},                   // Additional UI state
  });
  
  // Assistant mode - 'dashboard' or 'workspace'
  const [assistantMode, setAssistantMode] = useState('workspace');
  
  // Conversation history for the assistant
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Loading state for AI responses
  const [isLoading, setIsLoading] = useState(false);
  
  // Track if conversation needs saving
  const pendingSave = useRef(false);
  const saveTimeout = useRef(null);
  
  // Settings state
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Proactive suggestions state
  const [proactiveSuggestion, setProactiveSuggestion] = useState(null);
  const [showProactiveSuggestion, setShowProactiveSuggestion] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await authenticatedAxios.get('/workspace/assistant/settings');
        if (response.data?.settings) {
          setSettings(prev => ({ ...prev, ...response.data.settings }));
        }
        setSettingsLoaded(true);
      } catch (error) {
        // Settings endpoint might not exist yet - use defaults
        console.log('[WorkspaceAssistant] Using default settings');
        setSettingsLoaded(true);
      }
    };
    
    loadSettings();
  }, [authenticatedAxios]);

  // ============================================
  // CONTEXT UPDATE FUNCTIONS
  // ============================================

  /**
   * Update the current workspace context
   */
  const updateWorkspaceContext = useCallback((newContext) => {
    setWorkspaceContext(prev => ({
      ...prev,
      ...newContext,
      lastUpdated: Date.now()
    }));
    
    // Update assistant mode based on active tab
    if (newContext.activeTab) {
      if (newContext.activeTab === 'caseDashboard') {
        setAssistantMode('dashboard');
      } else {
        setAssistantMode('workspace');
      }
    }
  }, []);

  /**
   * Set the selected user/conversation
   */
  const setSelectedUser = useCallback((user, threadId) => {
    setWorkspaceContext(prev => ({
      ...prev,
      selectedUser: user,
      threadId: threadId,
      lastUpdated: Date.now()
    }));
  }, []);

  /**
   * Set the active tab
   */
  const setActiveTab = useCallback((tab) => {
    setWorkspaceContext(prev => ({
      ...prev,
      activeTab: tab,
      lastUpdated: Date.now()
    }));
    
    // Update assistant mode based on tab
    if (tab === 'caseDashboard') {
      setAssistantMode('dashboard');
    } else {
      setAssistantMode('workspace');
    }
  }, []);

  /**
   * Clear context when conversation ends
   */
  const clearContext = useCallback(() => {
    setWorkspaceContext({
      activeTab: null,
      selectedUser: null,
      threadId: null,
      rightPanelMode: null,
      uiState: {},
    });
  }, []);

  // ============================================
  // CONVERSATION PERSISTENCE
  // ============================================

  /**
   * Save conversation to backend (debounced)
   */
  const saveConversation = useCallback(async (messages) => {
    if (!messages || messages.length === 0) return;
    
    try {
      await authenticatedAxios.post('/workspace/assistant/conversation', {
        sessionId,
        messages,
        context: {
          activeTab: workspaceContext.activeTab || 'unknown',
          threadId: workspaceContext.threadId,
          assistantMode
        }
      });
      console.log('[WorkspaceAssistant] Conversation saved successfully');
    } catch (error) {
      console.error('[WorkspaceAssistant] Error saving conversation:', error);
    }
  }, [authenticatedAxios, sessionId, workspaceContext, assistantMode]);

  /**
   * Debounced save - waits 2 seconds after last message before saving
   */
  const debouncedSave = useCallback((messages) => {
    pendingSave.current = true;
    
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    saveTimeout.current = setTimeout(() => {
      if (pendingSave.current && messages.length > 0) {
        saveConversation(messages);
        pendingSave.current = false;
      }
    }, 2000);
  }, [saveConversation]);

  // ============================================
  // CONVERSATION MANAGEMENT
  // ============================================

  const addMessage = useCallback((message) => {
    setConversationHistory(prev => {
      const updated = [...prev, {
        ...message,
        timestamp: Date.now(),
        context: message.role === 'user' ? {
          activeTab: workspaceContext.activeTab,
          assistantMode
        } : undefined
      }];
      
      // Trigger debounced save
      debouncedSave(updated);
      
      return updated;
    });
  }, [workspaceContext, assistantMode, debouncedSave]);

  const clearConversation = useCallback(() => {
    // Save current conversation before clearing (if any messages)
    if (conversationHistory.length > 0) {
      saveConversation(conversationHistory);
    }
    
    // Clear and start new session
    setConversationHistory([]);
    setSessionId(generateSessionId());
    
    // Clear any pending save
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    pendingSave.current = false;
  }, [conversationHistory, saveConversation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (conversationHistory.length > 0 && pendingSave.current) {
        saveConversation(conversationHistory);
      }
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  // ============================================
  // SETTINGS MANAGEMENT
  // ============================================

  const updateSettings = useCallback(async (newSettings) => {
    const mergedSettings = { ...settings, ...newSettings };
    setSettings(mergedSettings);
    
    try {
      await authenticatedAxios.put('/workspace/assistant/settings', {
        settings: mergedSettings
      });
      console.log('[WorkspaceAssistant] Settings saved');
    } catch (error) {
      console.error('[WorkspaceAssistant] Error saving settings:', error);
    }
  }, [settings, authenticatedAxios]);

  // ============================================
  // PROACTIVE SUGGESTIONS
  // ============================================

  const dismissProactiveSuggestion = useCallback(() => {
    setShowProactiveSuggestion(false);
    setProactiveSuggestion(null);
  }, []);

  /**
   * Generate a proactive suggestion based on current context
   */
  const generateProactiveSuggestion = useCallback(() => {
    if (!settings.proactiveSuggestions || !workspaceContext.activeTab) {
      setProactiveSuggestion(null);
      return;
    }

    let suggestion = null;
    const { activeTab, selectedUser } = workspaceContext;
    
    if (!selectedUser) {
      suggestion = {
        type: 'info',
        text: 'Select a user from the list to view their case details.',
        action: 'select_user'
      };
    } else if (activeTab === 'caseDashboard') {
      suggestion = {
        type: 'tip',
        text: 'Review the readiness analysis to see what\'s needed before proceeding.',
        action: 'check_readiness'
      };
    } else if (activeTab === 'applicationReview') {
      suggestion = {
        type: 'tip',
        text: 'Run validation checks on all documents before submission.',
        action: 'validate_documents'
      };
    }

    setProactiveSuggestion(suggestion);
    setShowProactiveSuggestion(!!suggestion);
  }, [settings.proactiveSuggestions, workspaceContext]);

  // Generate suggestions when context changes
  useEffect(() => {
    if (settingsLoaded && settings.proactiveSuggestions) {
      const debounce = setTimeout(generateProactiveSuggestion, 1000);
      return () => clearTimeout(debounce);
    }
  }, [workspaceContext, settingsLoaded, settings.proactiveSuggestions, generateProactiveSuggestion]);

  // ============================================
  // GET FULL CONTEXT FOR AI
  // ============================================

  /**
   * Get the complete context object to send to AI
   */
  const getFullContext = useCallback(() => {
    return {
      workspace: workspaceContext,
      assistantMode,
      conversationHistory: conversationHistory.slice(-10),
      timestamp: Date.now()
    };
  }, [workspaceContext, assistantMode, conversationHistory]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // State
    isAssistantActive,
    setIsAssistantActive,
    workspaceContext,
    assistantMode,
    conversationHistory,
    isLoading,
    sessionId,
    
    // Context updates
    updateWorkspaceContext,
    setSelectedUser,
    setActiveTab,
    clearContext,
    setAssistantMode,
    
    // Conversation
    addMessage,
    clearConversation,
    setIsLoading,
    saveConversation,
    
    // Full context for AI
    getFullContext,
    
    // Settings
    settings,
    settingsLoaded,
    updateSettings,
    
    // Proactive Suggestions
    proactiveSuggestion,
    showProactiveSuggestion,
    dismissProactiveSuggestion,
  };

  return (
    <WorkspaceAssistantContext.Provider value={value}>
      {children}
    </WorkspaceAssistantContext.Provider>
  );
};

// ============================================
// CUSTOM HOOK
// ============================================

export const useWorkspaceAssistant = () => {
  const context = useContext(WorkspaceAssistantContext);
  if (!context) {
    throw new Error('useWorkspaceAssistant must be used within a WorkspaceAssistantProvider');
  }
  return context;
};

export default WorkspaceAssistantContext;

